import React, { useCallback, useEffect, useState } from "react";

import styles from "./indicatorTraffic.module.css";
import { getIndicatorBaseline } from "../../services/indicators";
import { addPorcentage, formatNumberToCurrency } from "../../utils/formatter";

const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

const STATUS_LABEL = {
  bom: "acima da média",
  estavel: "estável",
  atencao: "atenção",
  critico: "crítico",
  sem_base: "sem base de comparação"
};

// Indicadores que pioraram têm uma ação na fila que os trata.
const NEEDS_ACTION = ["atencao", "critico"];

const monthLabel = (value) => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return `${MONTHS[date.getMonth()]}/${date.getFullYear()}`;
};

const formatValue = (value, unit) => {
  if (value === null || value === undefined) {
    return "Sem dados";
  }
  if (unit === "currency") {
    return formatNumberToCurrency(Number(value));
  }
  if (unit === "percent") {
    return addPorcentage(Number(value));
  }
  return Number(value).toLocaleString("pt-BR");
};

// "0,0% vs média" lê como comparação contra zero. Quando não houve variação, o texto
// diz isso; sem baseline, cai no rótulo do status.
const deltaLabel = (delta, status) => {
  if (delta === null || delta === undefined) {
    return STATUS_LABEL[status] || STATUS_LABEL.sem_base;
  }

  const rounded = Number(delta).toFixed(1);

  if (rounded === "0.0" || rounded === "-0.0") {
    return "sem variação vs média de 3 meses";
  }

  const signal = Number(delta) > 0 ? "+" : "";
  return `${signal}${rounded.replace(".", ",")}% vs média de 3 meses`;
};

const IndicatorTraffic = ({ institutionId, onActionClick }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!institutionId) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setData(await getIndicatorBaseline(institutionId));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [institutionId]);

  useEffect(() => {
    load();
  }, [load]);

  const indicators = data?.indicators || [];

  if (loading) {
    return <p className={styles.note}>Carregando indicadores...</p>;
  }

  if (error) {
    return <p className={styles.error}>{error}</p>;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h3 className={styles.title}>Indicadores do mês fechado</h3>
        <span className={styles.subtitle}>
          Comparados com a média dos 3 meses anteriores da própria instituição. Não seguem o período filtrado.
        </span>
      </div>

      <div className={styles.grid}>
        {indicators.map((indicator) => {
          const status = indicator.status || "sem_base";
          const reference = monthLabel(indicator.refMonth);

          return (
            <section key={indicator.key} className={styles.tile} title={indicator.explanation}>
              <div className={styles.tileHeader}>
                <span className={styles.tileName}>{indicator.name}</span>
                <span className={styles.code}>{indicator.code}</span>
              </div>

              <span className={styles.value}>{formatValue(indicator.current, indicator.unit)}</span>

              <span className={`${styles.delta} ${styles[`text_${status}`]}`}>
                {deltaLabel(indicator.deltaPercent, status)}
              </span>

              <span className={styles.footer}>
                {reference ? `Referência ${reference}` : "Sem mês de referência"}
                {indicator.sameMonthLastYear != null && (
                  <>
                    {" · ano anterior "}
                    {formatValue(indicator.sameMonthLastYear, indicator.unit)}
                  </>
                )}
              </span>

              {NEEDS_ACTION.includes(status) && onActionClick && (
                <button type="button" className={styles.actionLink} onClick={onActionClick}>
                  Ver ações →
                </button>
              )}
            </section>
          );
        })}

        {indicators.length === 0 && (
          <p className={styles.note}>Sem série mensal suficiente para comparar. Importe ao menos três meses de dados.</p>
        )}
      </div>
    </div>
  );
};

export default IndicatorTraffic;
